import { FC, useState } from 'react';
import { LoginParams, useAppContext, useLogin } from '@frx/frx-react-context';
import { Navigate, useNavigate } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import { Center } from '@chakra-ui/react';
import { Loader, LoginForm } from '../../components';
import { isProd } from '../../utils';

export const Login: FC = () => {
  const {
    data: {
      authData: { loggedIn },
    },
  } = useAppContext();
  const navigate = useNavigate();
  const { mutate: login, isLoading } = useLogin();
  const [error, setError] = useState('');

  // Check USE_FRX_AUTHENTICATION env variable and ensure not running in Cypress
  const useFrxAuthentication =
    process.env.REACT_APP_USE_FRX_AUTHENTICATION === 'true' &&
    !(window as any).Cypress;

  const handleLogin = (values: LoginParams) => {
    const { userName, password } = values;
    setError('');
    login(
      { userName, password },
      {
        onSuccess: (data) => {
          navigate('/dashboard');
          Sentry.setUser({
            id: String(data?.cons_id),
            username: userName,
          });
        },
        onError: (err) => {
          const { code, message } = err;
          // The API returns 202 for an unauthenticated user, which is non typical.
          // Alternatively look for "invalid"
          // In the very exceptional case of a system failure display a fallback message.
          if (code === 202 || message.toLowerCase().includes('invalid')) {
            setError('Invalid username or password');
          } else {
            setError(
              'Sorry, we are not able to log you in at this time. Please try again later.'
            );
          }
        },
      }
    );
  };

  // go to dashboard if already logged in
  if (loggedIn) {
    return <Navigate to="/" />;
  }

  if (useFrxAuthentication) {
    console.log('Redirecting to FRX authentication...');
    window.location.href = `https://services${
      isProd ? '' : '.qa'
    }.stjude.org/frx-authentication/login?redirect=${encodeURIComponent(
      window.location.href
    )}`;
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  // default - display login form
  return (
    <LoginForm
      onLogin={handleLogin}
      errorMessage={error}
      isLoading={isLoading}
    />
  );
};
